import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { attendanceAPI } from '../services/api';
import toast from 'react-hot-toast';

// @attendance-portal-v1 (15 Sep 2026)
// Monthly doctor punctuality: when the doctor actually joined the video room
// compared with the booked start (source: Daily, captured hourly by the backend).
// A consultation is flagged when the doctor joined more than the late threshold
// after the start, or never joined at all.

const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280', fontWeight: 700, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
const td = { padding: '10px 12px', fontSize: 13, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };

const STATUS_EL = {
  completed: 'Ολοκληρώθηκε',
  no_show: 'DNA',
  in_progress: 'Σε εξέλιξη',
  cancelled_by_doctor: 'Μη προσέλευση γιατρού',
};

const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('el-GR', { timeZone: 'Europe/Athens', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('el-GR', { timeZone: 'Europe/Athens', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
const fmtMinutes = (m) => {
  if (m === null || m === undefined || m === '') return '—';
  const n = Number(m);
  if (Number.isNaN(n)) return '—';
  if (n < 0) return `${Math.abs(n).toFixed(1)}′ νωρίτερα`;
  return `+${n.toFixed(1)}′`;
};
const fmtDelay = (s) => (s === null || s === undefined) ? '—' : fmtMinutes(Number(s) / 60);

const Pill = ({ text, color }) => (
  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                 color, background: color + '18', whiteSpace: 'nowrap' }}>{text}</span>
);

export default function DoctorAttendancePage() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDoctor, setOpenDoctor] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setOpenDoctor(null);
    attendanceAPI.monthly(month)
      .then(({ data: res }) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) { setData(null); toast.error('Αδυναμία φόρτωσης στοιχείων προσέλευσης'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [month]);

  const late = data ? data.lateThresholdMinutes : 3;
  const doctors = (data && data.doctors) || [];
  const flagged = (data && data.flagged) || [];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Προσέλευση Γιατρών</h1>
          <p style={{ color: '#6b7280', fontSize: 13, margin: '6px 0 0' }}>
            Ώρα σύνδεσης του γιατρού στη βιντεοκλήση σε σχέση με την ώρα του ραντεβού.
            Καθυστέρηση άνω των {late} λεπτών ή μη σύνδεση επισημαίνεται.
          </p>
        </div>
        <label style={{ fontSize: 13, color: '#374151' }}>
          Μήνας{' '}
          <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)}
                 style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
        </label>
      </div>

      {data && data.missingData > 0 && (
        <div style={{ background: '#FEF3C7', color: '#92400E', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
          {data.missingData} συνεδρίες του μήνα δεν έχουν ακόμη δεδομένα σύνδεσης
          (καταγράφονται 2 ώρες μετά την έναρξη· παλαιότερες από το διαθέσιμο ιστορικό του Daily δεν μπορούν να ανακτηθούν).
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Γιατρός</th>
              <th style={th}>Συνεδρίες</th>
              <th style={th}>Μέσος χρόνος σύνδεσης</th>
              <th style={th}>Εκπρόθεσμες (&gt;{late}′)</th>
              <th style={th}>Δεν συνδέθηκε</th>
              <th style={th}>Μέγιστη καθυστέρηση</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td style={td} colSpan={7}>Φόρτωση…</td></tr>
            )}
            {!loading && doctors.length === 0 && (
              <tr><td style={td} colSpan={7}>Δεν υπάρχουν δεδομένα για αυτόν τον μήνα.</td></tr>
            )}
            {!loading && doctors.map((d) => {
              const issues = d.lateCount + d.neverJoined;
              const isOpen = openDoctor === d.doctorId;
              const rows = flagged.filter((f) => f.doctorId === d.doctorId);
              return (
                <React.Fragment key={d.doctorId}>
                  <tr style={{ background: issues > 0 ? '#FEF2F2' : 'transparent', cursor: issues > 0 ? 'pointer' : 'default' }}
                      onClick={() => issues > 0 && setOpenDoctor(isOpen ? null : d.doctorId)}>
                    <td style={td}><Link to={`/doctors/${d.doctorId}`} onClick={(e) => e.stopPropagation()}>{d.doctorName}</Link></td>
                    <td style={td}>{d.consultations}</td>
                    <td style={td}>{fmtMinutes(d.avgDelayMinutes)}</td>
                    <td style={td}>{d.lateCount > 0 ? <Pill text={d.lateCount} color="#C0185A" /> : 0}</td>
                    <td style={td}>{d.neverJoined > 0 ? <Pill text={d.neverJoined} color="#B91C1C" /> : 0}</td>
                    <td style={td}>{fmtDelay(d.maxDelaySeconds)}</td>
                    <td style={{ ...td, color: '#6b7280' }}>{issues > 0 ? (isOpen ? '▲' : '▼') : ''}</td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={7} style={{ padding: '4px 12px 14px', background: '#FFFBFB', borderBottom: '1px solid #f1f5f9' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={th}>Ραντεβού</th>
                              <th style={th}>Σύνδεση γιατρού</th>
                              <th style={th}>Καθυστέρηση</th>
                              <th style={th}>Σύνδεση ασθενή</th>
                              <th style={th}>Κατάσταση</th>
                              <th style={th}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((f) => (
                              <tr key={f.appointmentId}>
                                <td style={td}>{fmtDateTime(f.scheduledStart)}</td>
                                <td style={td}>{f.doctorFirstJoinAt ? fmtTime(f.doctorFirstJoinAt) : <Pill text="Δεν συνδέθηκε" color="#B91C1C" />}</td>
                                <td style={td}>{fmtDelay(f.delaySeconds)}</td>
                                <td style={td}>{fmtTime(f.patientFirstJoinAt)}</td>
                                <td style={td}>{STATUS_EL[f.status] || f.status}</td>
                                <td style={td}><Link to={`/consultations/${f.appointmentId}`}>Προβολή</Link></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
